import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGoogleMap } from "#/hook/useGoogleMap";
import { getMarkerIconSvg, getWarehouseIconSvg } from "./mapIcons";
import { MARKER_ICON_SIZE } from "./mapConstants";

export interface RouteStop {
  /** Shown inside the marker, e.g. the stop's order on the route. */
  label: string;
  title: string;
  latitude: number | string | null;
  longitude: number | string | null;
  /** 1-based trip that carries the stop; legs of trip 2 and later are dashed. Omit for one trip. */
  tripIndex?: number | null;
}

interface Props {
  /** Where the route starts. Drawn as the platform's map pin, in dark grey. */
  origin: RouteStop | null;
  /** Stops in driving order. Stops without coordinates are skipped. */
  stops: RouteStop[];
  /** Default 20 rem. Any Tailwind height class. */
  className?: string;
  /** Also draw the drive back from the last stop to the origin, dashed. */
  showReturn?: boolean;
  /** Colour of the road through the stops; default the platform blue. */
  routeColor?: string;
  /** Colour of the way back; default the pin's dark grey. */
  returnColor?: string;
  /** Outline drawn under the solid legs, a darker shade of the route colour; default white. */
  casingColor?: string;
  /** How the origin is drawn: the platform pin, or the warehouse circle of the trip timeline. */
  originMarker?: "pin" | "warehouse";
  /** Tone the basemap down so the route stands out. */
  muted?: boolean;
}

const MAP_OPTIONS: google.maps.MapOptions = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};
/**
 * A muted basemap for route maps: the map's own colours are toned down (not
 * greyscale) and the POI pins hidden, so the route and the markers are the
 * only vivid things on it. Same idea as the route planners' grey basemaps.
 */
const MUTED_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ saturation: -55 }, { lightness: 10 }] },
  { elementType: "labels.text.fill", stylers: [{ saturation: -60 }, { lightness: 15 }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ saturation: -70 }, { lightness: 20 }],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ saturation: -40 }, { lightness: 15 }],
  },
];
const PIN_COLOR = "#1f2937";
/** Fixed route colour for every client (Ammad, 2026-09-28), the blue of the platform's default accent. */
const ROUTE_COLOR = "#2563eb";
const FIT_PADDING = 48;
const DASH = [
  {
    icon: { path: "M 0,-1 0,1", strokeOpacity: 0.95, strokeWeight: 5, scale: 3 },
    offset: "0",
    repeat: "16px",
  },
];

function toCoordinate(value: number | string | null): number | null {
  if (value === null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
}

/**
 * A route on a Google map, drawn the way route planners do: one fixed blue
 * for the road from the origin through the stops in
 * order, dark numbered circles for the stops, the platform's map pin for the origin.
 * Legs that belong to a second or later trip are dashed. The way back is drawn
 * only when asked (`showReturn`), dashed in the pin's grey. When
 * Directions is unavailable (key not allowed, quota, no road) the points are
 * joined with thin straight lines and a note says so. The map re-fits to the
 * road once it arrives. Validates coordinates itself.
 */
const RouteMap = ({
  origin,
  stops,
  className = "h-80",
  showReturn = false,
  routeColor = ROUTE_COLOR,
  returnColor = PIN_COLOR,
  casingColor = "#ffffff",
  originMarker = "pin",
  muted = false,
}: Props) => {
  const { t } = useTranslation();
  const { isLoaded } = useGoogleMap();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [directionsFailed, setDirectionsFailed] = useState(false);

  const points = useMemo(() => {
    const resolve = (stop: RouteStop | null) => {
      if (!stop) return null;
      const latitude = toCoordinate(stop.latitude);
      const longitude = toCoordinate(stop.longitude);
      return latitude !== null && longitude !== null
        ? { ...stop, position: { lat: latitude, lng: longitude } }
        : null;
    };
    return {
      origin: resolve(origin),
      stops: stops.map(resolve).filter((stop) => stop !== null),
    };
  }, [origin, stops]);

  const signature = useMemo(
    () =>
      [points.origin, ...points.stops]
        .map((point) => (point ? `${point.position.lat},${point.position.lng}` : "-"))
        .join("|") + (showReturn ? "|return" : ""),
    [points, showReturn]
  );
  /** With the way back, the route ends at the origin and every stop is a waypoint. */
  const withReturn = showReturn && points.origin !== null;

  /** Fit the frame to the road when known, else to the points. */
  const fitBounds = useCallback(
    (map: google.maps.Map, result: google.maps.DirectionsResult | null) => {
      const roadBounds = result?.routes[0]?.bounds;
      if (roadBounds) {
        map.fitBounds(roadBounds, FIT_PADDING);
        return;
      }
      const bounds = new google.maps.LatLngBounds();
      for (const point of [points.origin, ...points.stops]) {
        if (point) bounds.extend(point.position);
      }
      if (!bounds.isEmpty()) map.fitBounds(bounds, FIT_PADDING);
    },
    [points]
  );

  // Ask Directions once per distinct set of points; re-fit when the road arrives.
  useEffect(() => {
    if (!isLoaded || points.stops.length === 0) return;
    const start = points.origin ?? points.stops[0];
    const route = points.stops.filter((stop) => stop !== start);
    if (route.length === 0) return;
    const destination = withReturn ? start.position : route.at(-1)!.position;
    const between = (withReturn ? route : route.slice(0, -1)).map((stop) => ({
      location: stop.position,
      stopover: true,
    }));
    let cancelled = false;
    setDirections(null);
    setDirectionsFailed(false);
    new google.maps.DirectionsService()
      .route({
        origin: start.position,
        destination,
        waypoints: between,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      })
      .then((result) => {
        if (cancelled) return;
        setDirections(result);
        // Fit after the browser has laid the frame out, so the road fills it.
        requestAnimationFrame(() => {
          if (!cancelled && mapRef.current) fitBounds(mapRef.current, result);
        });
      })
      .catch(() => {
        if (!cancelled) setDirectionsFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // `signature` captures every coordinate; `points` itself changes identity on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, signature]);

  // One path per leg (origin → stop 1, stop 1 → stop 2 …), from the steps.
  const legPaths = useMemo(
    () =>
      directions?.routes[0]?.legs.map((leg) =>
        leg.steps.flatMap((step) => step.path.map((point) => point.toJSON()))
      ) ?? [],
    [directions]
  );
  const straightPath = useMemo(() => {
    const line = points.stops.map((stop) => stop.position);
    if (points.origin) line.unshift(points.origin.position);
    if (withReturn && points.origin) line.push(points.origin.position);
    return line;
  }, [points, withReturn]);
  /** The leg at `index` ends at stop `index`; dashed when that stop rides a later trip. */
  const legIsLaterTrip = (index: number) => (points.stops[index]?.tripIndex ?? 1) > 1;
  /** With the way back, the last leg goes home to the origin. */
  const legIsReturn = (index: number) => withReturn && index === legPaths.length - 1;
  const legOptions = (index: number): google.maps.PolylineOptions => {
    if (legIsReturn(index)) {
      return {
        strokeColor: returnColor,
        strokeOpacity: 0,
        strokeWeight: 6,
        zIndex: 1,
        icons: DASH,
      };
    }
    if (legIsLaterTrip(index)) {
      return {
        strokeColor: routeColor,
        strokeOpacity: 0,
        strokeWeight: 6,
        zIndex: 1,
        icons: DASH,
      };
    }
    return { strokeColor: routeColor, strokeOpacity: 1, strokeWeight: 5, zIndex: 1 };
  };
  const legKind = (index: number) =>
    legIsReturn(index) ? "return" : legIsLaterTrip(index) ? "dashed" : "solid";

  if (!isLoaded) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-400 ${className}`}
      >
        {t("common.loading")}
      </div>
    );
  }
  if (points.stops.length === 0 && !points.origin) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500 ${className}`}
      >
        {t("routeMap.noCoordinates")}
      </div>
    );
  }

  const label = (text: string): google.maps.MarkerLabel => ({
    text,
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "700",
  });
  const pin = (path: google.maps.SymbolPath, scale: number) => ({
    path,
    scale,
    fillColor: PIN_COLOR,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
  });

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg border border-gray-200 ${className}`}
    >
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        options={muted ? { ...MAP_OPTIONS, styles: MUTED_MAP_STYLES } : MAP_OPTIONS}
        onLoad={(map) => {
          mapRef.current = map;
          fitBounds(map, directions);
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
      >
        {points.origin && (
          <Marker
            position={points.origin.position}
            title={points.origin.title}
            zIndex={2}
            icon={
              originMarker === "warehouse"
                ? {
                    url: getWarehouseIconSvg(PIN_COLOR),
                    scaledSize: new google.maps.Size(MARKER_ICON_SIZE, MARKER_ICON_SIZE),
                    anchor: new google.maps.Point(MARKER_ICON_SIZE / 2, MARKER_ICON_SIZE / 2),
                  }
                : {
                    url: getMarkerIconSvg(PIN_COLOR),
                    scaledSize: new google.maps.Size(MARKER_ICON_SIZE, MARKER_ICON_SIZE),
                    anchor: new google.maps.Point(MARKER_ICON_SIZE / 2, MARKER_ICON_SIZE),
                  }
            }
          />
        )}
        {points.stops.map((stop) => (
          <Marker
            key={`${stop.label}-${stop.position.lat}-${stop.position.lng}`}
            position={stop.position}
            title={stop.title}
            zIndex={3}
            icon={pin(google.maps.SymbolPath.CIRCLE, 13)}
            label={label(stop.label)}
          />
        ))}
        {/* One line object per leg and one for the fallback: Google keeps options
            across updates, so a dashed and a solid line never share an instance. */}
        {/* White casing under the solid legs, so the route separates from the map's own colours. */}
        {legPaths.map((path, index) =>
          legKind(index) === "solid" ? (
            <Polyline
              key={`casing-${index}`}
              path={path}
              options={{ strokeColor: casingColor, strokeOpacity: 1, strokeWeight: 8, zIndex: 0 }}
            />
          ) : null
        )}
        {legPaths.map((path, index) => (
          <Polyline
            key={`leg-${index}-${legKind(index)}`}
            path={path}
            options={legOptions(index)}
          />
        ))}
        {directionsFailed && straightPath.length > 1 && (
          <Polyline
            key="straight"
            path={straightPath}
            options={{ strokeColor: routeColor, strokeOpacity: 0.6, strokeWeight: 2, zIndex: 1 }}
          />
        )}
      </GoogleMap>
      {directionsFailed && (
        <span className="absolute bottom-2 left-2 rounded bg-white/90 px-2 py-1 text-xs text-gray-600 shadow">
          {t("routeMap.straightLines")}
        </span>
      )}
    </div>
  );
};

export default RouteMap;
